import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface DuplicateTabMessage {
  type: 'TAB_OPENED' | 'TAB_CLOSED' | 'PRIMARY_CHECK' | 'PRIMARY_RESPONSE';
  tabId: string;
  isPrimary?: boolean;
  timestamp: number;
}

/**
 * Service to detect and manage duplicate tabs
 * 
 * Fallback Behavior:
 * - When localStorage is unavailable (disabled, quota exceeded, etc.), the service
 *   automatically falls back to treating all tabs as primary tabs
 * - This ensures the application remains functional even without localStorage
 * - The service attempts to recover localStorage functionality when tabs become visible
 */
@Injectable({
  providedIn: 'root'
})
export class DuplicateTabGuardService {
  private readonly STORAGE_KEY = 'enact_tab_manager';
  private readonly SESSION_STORAGE_KEY = 'enact_tab_id';
  private readonly HEARTBEAT_INTERVAL = 5000; // 5 seconds
  private readonly TAB_TIMEOUT = 10000; // 10 seconds
  
  private tabId: string;
  private isPrimaryTab = false;
  private heartbeatInterval: any;
  private isDuplicateTab$ = new BehaviorSubject<boolean>(false);
  private primaryTabLost$ = new BehaviorSubject<boolean>(false);
  private isCheckingStatus = false;
  private statusCheckDelay = 500; // Delay before showing duplicate error
  private localStorageAvailable = true; // Track localStorage availability
  private sessionStorageAvailable = true;
  
  // Performance optimizations
  private cachedTabs: any[] = []; // Cache tabs data to reduce localStorage reads
  private lastCacheUpdate = 0;
  private readonly CACHE_TTL = 1000; // Cache valid for 1 second
  private debounceTimer: any;
  private promotionLockTimer: any; // Prevent simultaneous primary promotions
  private readonly PROMOTION_LOCK_KEY = 'enact_promotion_lock';
  private readonly PROMOTION_LOCK_TTL = 2000; // 2 seconds

  constructor() {
    this.tabId = this.restoreTabIdFromSession();
    this.checkLocalStorageAvailability();

    // Special override for /web/reports/ URLs
    if (window.location.href.includes('/web/reports/')) {
      // Always allow this tab, never trigger duplicate error
      this.setPrimaryTab(true);
      this.isDuplicateTab$.next(false);
      // Optionally, open the second tab (index 1) if not already
      // This logic assumes a tab manager exists elsewhere; here we just signal
      setTimeout(() => {
        // Dispatch a custom event or call a handler to open the second tab
        // (Replace with your actual tab manager logic if needed)
        window.dispatchEvent(new CustomEvent('openSecondTabForReports'));
      }, 0);
      return; // Skip all other duplicate tab logic
    }
    this.initializeTabDetection();
  }

  /**
   * Restore a stable tab ID from sessionStorage so reloads keep the same identity
   */
  private restoreTabIdFromSession(): string {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      this.sessionStorageAvailable = false;
      return this.generateTabId();
    }

    if (!this.ensureSessionStorageAvailability()) {
      return this.generateTabId();
    }

    try {
      const existing = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (existing) {
        return existing;
      }

      const newId = this.generateTabId();
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, newId);
      return newId;
    } catch (error) {
      console.warn('sessionStorage is not available, falling back to ephemeral tab IDs:', error);
      this.sessionStorageAvailable = false;
      return this.generateTabId();
    }
  }

  /**
   * Verify sessionStorage availability once per tab lifecycle
   */
  private ensureSessionStorageAvailability(): boolean {
    if (!this.sessionStorageAvailable || typeof sessionStorage === 'undefined') {
      return false;
    }

    try {
      const testKey = 'enact_ss_test';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      this.sessionStorageAvailable = false;
      return false;
    }
  }

  /**
   * Check if localStorage is available and working
   */
  private checkLocalStorageAvailability(): void {
    try {
      const testKey = 'enact_ls_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this.localStorageAvailable = true;
    } catch (error) {
      console.warn('localStorage is not available, falling back to primary tab mode:', error);
      this.localStorageAvailable = false;
      // When localStorage is not available, always assume primary tab to allow functionality
      this.setPrimaryTab(true);
    }
  }

  /**
   * Returns observable that emits true when current tab is detected as duplicate
   */
  isDuplicateTab(): Observable<boolean> {
    return this.isDuplicateTab$.asObservable();
  }

  /**
   * Returns observable that emits true when primary tab is lost
   */
  isPrimaryTabLost(): Observable<boolean> {
    return this.primaryTabLost$.asObservable();
  }

  /**
   * Check if current tab is primary
   */
  isPrimary(): boolean {
    return this.isPrimaryTab;
  }

  /**
   * Get current tab ID
   */
  getTabId(): string {
    return this.tabId;
  }

  /**
   * Check if localStorage is available and working
   */
  isLocalStorageAvailable(): boolean {
    return this.localStorageAvailable;
  }

  /**
   * Manually attempt to recover localStorage (can be called externally)
   */
  retryLocalStorage(): boolean {
    this.attemptLocalStorageRecovery();
    return this.localStorageAvailable;
  }

  /**
   * Initialize duplicate tab detection
   */
  private initializeTabDetection(): void {
    // Check for existing tabs
    this.checkForExistingTabs();
    
    // Listen for storage events (messages from other tabs)
    this.setupStorageListener();
    
    // Setup heartbeat to maintain tab presence
    this.startHeartbeat();
    
    // Handle tab/window closing
    this.setupUnloadListener();
    
    // Handle tab visibility changes
    this.setupVisibilityListener();
  }

  /**
   * Generate unique tab ID (optimized)
   */
  private generateTabId(): string {
    // Use performance.now() for better precision and avoid string operations
    const timestamp = performance.now();
    const random = Math.random() * 0x10000 | 0; // Convert to integer for efficiency
    return `tab_${timestamp}_${random.toString(16)}`;
  }

  /**
   * Check for existing tabs and determine if current tab should be primary
   * BULLETPROOF APPROACH: Simple first-come-first-served with immediate enforcement
   */
  private checkForExistingTabs(): void {
    // Prevent rapid status changes
    if (this.isCheckingStatus) {
      return;
    }
    
    this.isCheckingStatus = true;
    
    // If localStorage is not available, this tab becomes primary
    if (!this.localStorageAvailable) {
      this.setPrimaryTab(true);
      this.isCheckingStatus = false;
      return;
    }
    
    try {
      // Get current timestamp for this check
      const now = Date.now();
      
      // Try to claim primary status atomically
      const existingPrimary = localStorage.getItem('enact_primary_tab');
      const primaryData = existingPrimary ? JSON.parse(existingPrimary) : null;
      
      // Check if existing primary is still active (heartbeat within last 10 seconds)
      const primaryIsActive = primaryData && (now - primaryData.lastHeartbeat) < 10000;
      
      const currentTabOwnsPrimary = primaryData && primaryData.tabId === this.tabId;

      if (!primaryIsActive || currentTabOwnsPrimary) {
        // No active primary exists or this tab previously owned the lock (e.g., crashed reload)
        this.claimPrimaryStatus();
        this.setPrimaryTab(true);
      } else {
        // Active primary exists, this tab is duplicate
        this.setPrimaryTab(false);
      }
      
    } catch (error) {
      console.error('Error checking for existing tabs:', error);
      // On error, become primary as fallback
      this.setPrimaryTab(true);
    }
    
    this.isCheckingStatus = false;
  }

  /**
   * Claim primary status in localStorage
   */
  private claimPrimaryStatus(): void {
    if (!this.localStorageAvailable) return;
    
    try {
      const primaryData = {
        tabId: this.tabId,
        lastHeartbeat: Date.now(),
        url: window.location.href
      };
      localStorage.setItem('enact_primary_tab', JSON.stringify(primaryData));
    } catch (error) {
      console.error('Error claiming primary status:', error);
    }
  }

  /**
   * Set primary tab status - BULLETPROOF VERSION
   */
  private setPrimaryTab(isPrimary: boolean): void {
    this.isPrimaryTab = isPrimary;
    
    if (isPrimary) {
      // This is the primary tab
      this.isDuplicateTab$.next(false);
    } else {
      // This is a duplicate tab
      this.isDuplicateTab$.next(true);
    }
  }

  /**
   * Schedule a status update with delay to prevent flickering
   */
  private scheduleStatusUpdate(): void {
    setTimeout(() => {
      if (!this.isPrimaryTab) {
        this.isDuplicateTab$.next(true);
      }
    }, this.statusCheckDelay);
  }

  /**
   * Get active tabs from localStorage with caching
   */
  private getActiveTabsFromStorage(): any[] {
    if (!this.localStorageAvailable) {
      return [];
    }
    
    // Return cached data if still valid
    const now = Date.now();
    if (now - this.lastCacheUpdate < this.CACHE_TTL && this.cachedTabs.length >= 0) {
      return this.filterActiveTabs(this.cachedTabs);
    }
    
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      this.cachedTabs = stored ? JSON.parse(stored) : [];
      this.lastCacheUpdate = now;
      return this.filterActiveTabs(this.cachedTabs);
    } catch (error: any) {
      console.error('Error reading tabs from storage:', error);
      this.localStorageAvailable = false;
      console.warn('localStorage became unavailable, switching to primary tab mode');
      this.setPrimaryTab(true);
      return [];
    }
  }

  /**
   * Filter out inactive tabs based on last heartbeat
   */
  private filterActiveTabs(tabs: any[]): any[] {
    const now = Date.now();
    return tabs.filter(tab => (now - tab.lastHeartbeat) < this.TAB_TIMEOUT);
  }

  /**
   * Register current tab in storage (optimized)
   */
  private registerCurrentTab(): void {
    // If localStorage is not available, skip registration
    if (!this.localStorageAvailable) {
      return;
    }
    
    const activeTabs = this.getActiveTabsFromStorage();
    
    // Check if tab already exists to avoid duplicates
    const existingTabIndex = activeTabs.findIndex(tab => tab.tabId === this.tabId);
    
    if (existingTabIndex !== -1) {
      // Update existing tab instead of adding duplicate
      activeTabs[existingTabIndex] = {
        tabId: this.tabId,
        isPrimary: this.isPrimaryTab,
        lastHeartbeat: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
    } else {
      // Add new tab
      activeTabs.push({
        tabId: this.tabId,
        isPrimary: this.isPrimaryTab,
        lastHeartbeat: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    }
    
    this.saveTabsToStorage(activeTabs);
  }

  /**
   * Save tabs array to localStorage with cache invalidation
   */
  private saveTabsToStorage(tabs: any[]): void {
    if (!this.localStorageAvailable) {
      return;
    }
    
    try {
      const serialized = JSON.stringify(tabs);
      localStorage.setItem(this.STORAGE_KEY, serialized);
      
      // Update cache immediately to avoid unnecessary reads
      this.cachedTabs = tabs;
      this.lastCacheUpdate = Date.now();
    } catch (error: any) {
      console.error('Error saving tabs to storage:', error);
      
      // Check if this is a quota exceeded error
      if (error?.name === 'QuotaExceededError' || 
          error?.code === 22 || 
          error?.code === 1014) {
        console.warn('localStorage quota exceeded, switching to primary tab mode');
        this.localStorageAvailable = false;
        this.setPrimaryTab(true);
      } else {
        console.warn('localStorage error occurred, switching to primary tab mode');
        this.localStorageAvailable = false;
        this.setPrimaryTab(true);
      }
    }
  }

  /**
   * Setup storage event listener for cross-tab communication
   */
  private setupStorageListener(): void {
    // If localStorage is not available, skip storage listener setup
    if (!this.localStorageAvailable) {
      return;
    }
    
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter(event => event.key === this.STORAGE_KEY && event.newValue !== null)
      )
      .subscribe((event) => {
        // Double-check localStorage availability before handling storage changes
        if (this.localStorageAvailable) {
          this.handleStorageChange(event);
        }
      });
  }

  /**
   * Handle storage changes (messages from other tabs) - optimized
   */
  private handleStorageChange(event: StorageEvent): void {
    try {
      const tabs = JSON.parse(event.newValue || '[]');
      
      // Invalidate cache since storage changed externally
      this.cachedTabs = tabs;
      this.lastCacheUpdate = Date.now();
      
      const currentTab = tabs.find((tab: any) => tab.tabId === this.tabId);
      
      if (!currentTab) {
        // Current tab was removed, might need to become primary
        this.checkForPrimaryPromotion(tabs);
      } else {
        // Update current tab status if changed
        if (currentTab.isPrimary !== this.isPrimaryTab) {
          this.setPrimaryTab(currentTab.isPrimary);
          
          // If promoted to primary, clear duplicate status with small delay to prevent flickering
          if (currentTab.isPrimary) {
            setTimeout(() => {
              this.isDuplicateTab$.next(false);
            }, 200);
            this.primaryTabLost$.next(false);
          }
        }
      }
    } catch (error) {
      console.error('Error handling storage change:', error);
    }
  }

  /**
   * Check if current tab should be promoted to primary (with atomic promotion)
   */
  private checkForPrimaryPromotion(tabs: any[]): void {
    const activeTabs = this.filterActiveTabs(tabs);
    const hasPrimary = activeTabs.some(tab => tab.isPrimary);
    
    if (!hasPrimary && activeTabs.length > 0) {
      // No primary tab exists, attempt atomic promotion
      this.attemptAtomicPrimaryPromotion(activeTabs);
    }
  }

  /**
   * Start heartbeat to maintain tab presence - BULLETPROOF VERSION
   */
  private startHeartbeat(): void {
    // Start heartbeat only for primary tabs
    this.heartbeatInterval = setInterval(() => {
      this.updateHeartbeat();
    }, this.HEARTBEAT_INTERVAL);
    
    // Initial heartbeat
    this.updateHeartbeat();
  }

  /**
   * Update heartbeat - BULLETPROOF VERSION
   */
  private updateHeartbeat(): void {
    if (!this.localStorageAvailable || !this.isPrimaryTab) {
      return;
    }
    
    try {
      // Only primary tabs update their heartbeat
      const primaryData = {
        tabId: this.tabId,
        lastHeartbeat: Date.now(),
        url: window.location.href
      };
      localStorage.setItem('enact_primary_tab', JSON.stringify(primaryData));
      
      // Also check if we're still the rightful primary
      this.validatePrimaryStatus();
      
    } catch (error) {
      console.error('Error updating heartbeat:', error);
      this.localStorageAvailable = false;
    }
  }

  /**
   * Validate that this tab should still be primary
   */
  private validatePrimaryStatus(): void {
    if (!this.isPrimaryTab) return;
    
    try {
      const existingPrimary = localStorage.getItem('enact_primary_tab');
      const primaryData = existingPrimary ? JSON.parse(existingPrimary) : null;
      
      // If another tab has claimed primary status, demote this tab
      if (primaryData && primaryData.tabId !== this.tabId) {
        this.setPrimaryTab(false);
      }
    } catch (error) {
    }
  }
  
  /**
   * Perform the actual heartbeat update
   */
  private performHeartbeatUpdate(): void {
    if (!this.localStorageAvailable) {
      return;
    }
    
    const activeTabs = this.getActiveTabsFromStorage();
    
    // Find current tab and update heartbeat
    const currentTabIndex = activeTabs.findIndex(tab => tab.tabId === this.tabId);
    
    if (currentTabIndex !== -1) {
      // Only update if necessary to reduce localStorage writes
      const currentTab = activeTabs[currentTabIndex];
      const now = Date.now();
      const currentUrl = window.location.href;
      
      let needsUpdate = false;
      
      // Check if heartbeat is significantly outdated (> 3 seconds)
      if (now - currentTab.lastHeartbeat > 3000) {
        currentTab.lastHeartbeat = now;
        needsUpdate = true;
      }
      
      // Check if URL changed
      if (currentTab.url !== currentUrl) {
        currentTab.url = currentUrl;
        needsUpdate = true;
      }
      
      // Check if primary status changed
      if (currentTab.isPrimary !== this.isPrimaryTab) {
        currentTab.isPrimary = this.isPrimaryTab;
        needsUpdate = true;
      }
      
      if (!needsUpdate) {
        return; // Skip localStorage write if nothing changed
      }
    } else {
      // Tab not found, re-register
      this.registerCurrentTab();
      return;
    }
    
    // Optimize primary tab status checking
    this.optimizePrimaryTabStatus(activeTabs);
    
    this.saveTabsToStorage(activeTabs);
  }
  
  /**
   * Optimize primary tab status checking with atomic promotion
   */
  private optimizePrimaryTabStatus(activeTabs: any[]): void {
    const primaryTabs = activeTabs.filter(tab => tab.isPrimary);
    
    if (primaryTabs.length === 0) {
      // No primary tab exists, attempt atomic promotion
      this.attemptAtomicPrimaryPromotion(activeTabs);
    } else if (primaryTabs.length > 1) {
      // Multiple primary tabs, keep only this one if it's primary
      if (this.isPrimaryTab) {
        activeTabs.forEach(tab => {
          if (tab.tabId !== this.tabId) {
            tab.isPrimary = false;
          }
        });
      } else {
        // Another tab should be primary
        this.setPrimaryTab(false);
        this.scheduleStatusUpdate();
      }
    } else {
      // Exactly one primary tab exists
      const primaryTab = primaryTabs[0];
      if (primaryTab.tabId === this.tabId && !this.isPrimaryTab) {
        this.setPrimaryTab(true);
      } else if (primaryTab.tabId !== this.tabId && this.isPrimaryTab) {
        this.setPrimaryTab(false);
        this.scheduleStatusUpdate();
      }
    }
  }

  /**
   * Attempt atomic primary tab promotion with random delay to prevent race conditions
   */
  private attemptAtomicPrimaryPromotion(activeTabs: any[]): void {
    // Check if there's already a promotion in progress
    if (this.isPromotionLocked()) {
      return;
    }

    // Random delay between 50-200ms to prevent simultaneous promotions
    const randomDelay = Math.floor(Math.random() * 150) + 50;
    
    setTimeout(() => {
      this.executeAtomicPromotion(activeTabs);
    }, randomDelay);
  }

  /**
   * Check if promotion is currently locked
   */
  private isPromotionLocked(): boolean {
    if (!this.localStorageAvailable) {
      return false;
    }

    try {
      const lockData = localStorage.getItem(this.PROMOTION_LOCK_KEY);
      if (!lockData) {
        return false;
      }

      const { timestamp, tabId } = JSON.parse(lockData);
      const now = Date.now();

      // Check if lock is expired
      if (now - timestamp > this.PROMOTION_LOCK_TTL) {
        localStorage.removeItem(this.PROMOTION_LOCK_KEY);
        return false;
      }

      // If this tab owns the lock, it's not locked for this tab
      return tabId !== this.tabId;
    } catch (error) {
      // If there's an error reading the lock, assume it's not locked
      return false;
    }
  }

  /**
   * Execute atomic primary tab promotion
   */
  private executeAtomicPromotion(activeTabs: any[]): void {
    if (!this.localStorageAvailable) {
      // Fallback: just promote this tab
      this.setPrimaryTab(true);
      return;
    }

    try {
      // Acquire promotion lock
      const lockData = {
        timestamp: Date.now(),
        tabId: this.tabId
      };
      localStorage.setItem(this.PROMOTION_LOCK_KEY, JSON.stringify(lockData));

      // Double-check that no primary tab was created while we were waiting
      const currentTabs = this.getActiveTabsFromStorage();
      const currentPrimaryTabs = currentTabs.filter(tab => tab.isPrimary);

      if (currentPrimaryTabs.length === 0) {
        // Find the oldest tab (should be promoted to primary)
        const oldestTab = currentTabs.reduce((oldest, current) => 
          current.lastHeartbeat < oldest.lastHeartbeat ? current : oldest
        );

        if (oldestTab.tabId === this.tabId) {
          // This tab is the oldest, promote it
          this.setPrimaryTab(true);
          oldestTab.isPrimary = true;
          this.saveTabsToStorage(currentTabs);
        }
      }

      // Release the lock after a short delay
      setTimeout(() => {
        try {
          localStorage.removeItem(this.PROMOTION_LOCK_KEY);
        } catch (error) {
          console.debug('Error releasing promotion lock:', error);
        }
      }, 500);

    } catch (error) {
      console.error('Error during atomic promotion:', error);
      // Fallback: promote this tab anyway
      this.setPrimaryTab(true);
    }
  }

  /**
   * Setup window unload listener
   */
  private setupUnloadListener(): void {
    const cleanup = () => {
      
      // If this is the primary tab, clear the primary status
      if (this.isPrimaryTab && this.localStorageAvailable) {
        try {
          localStorage.removeItem('enact_primary_tab');
        } catch (error) {
          console.error('Error clearing primary status on close:', error);
        }
      }
      
      // Clear heartbeat interval
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
      }
    };

    // Handle page unload
    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('unload', cleanup);
    
    // Handle page hide (for mobile browsers)
    window.addEventListener('pagehide', cleanup);
  }

  /**
   * Attempt to recover localStorage functionality
   */
  private attemptLocalStorageRecovery(): void {
    try {
      const testKey = 'enact_ls_recovery_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      if (!this.localStorageAvailable) {
        this.localStorageAvailable = true;
        // Re-run initial tab detection since localStorage is now available
        this.checkForExistingTabs();
        this.registerCurrentTab();
      }
    } catch (error) {
      // localStorage is still not available, remain in fallback mode
      console.debug('localStorage still not available:', error);
    }
  }

  /**
   * Setup visibility change listener
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Try to recover localStorage if it was unavailable
        if (!this.localStorageAvailable) {
          this.attemptLocalStorageRecovery();
        }
        
        // Re-check our status - maybe the primary tab closed while we were hidden
        this.recheckTabStatus();
      }
    });
  }

  /**
   * Re-check tab status when becoming visible
   */
  private recheckTabStatus(): void {
    if (!this.localStorageAvailable) {
      return;
    }

    try {
      const existingPrimary = localStorage.getItem('enact_primary_tab');
      const primaryData = existingPrimary ? JSON.parse(existingPrimary) : null;
      const now = Date.now();
      
      // Check if existing primary is still active
      const primaryIsActive = primaryData && (now - primaryData.lastHeartbeat) < 10000;
      
      if (!primaryIsActive && !this.isPrimaryTab) {
        // No active primary exists and we're not primary, try to claim it
        this.claimPrimaryStatus();
        this.setPrimaryTab(true);
      } else if (primaryIsActive && primaryData.tabId !== this.tabId && this.isPrimaryTab) {
        // Another tab is the active primary, we should become duplicate
        this.setPrimaryTab(false);
      }
      
    } catch (error) {
      console.error('Error rechecking tab status:', error);
    }
  }

  /**
   * Remove current tab from storage
   */
  private removeCurrentTabFromStorage(): void {
    // If localStorage is not available, skip removal
    if (!this.localStorageAvailable) {
      return;
    }
    
    try {
      const existingTabs = this.getActiveTabsFromStorage();
      const filteredTabs = existingTabs.filter(tab => tab.tabId !== this.tabId);
      this.saveTabsToStorage(filteredTabs);
    } catch (error: any) {
      console.error('Error removing tab from storage:', error);
    }
  }

  /**
   * Cleanup resources (optimized)
   */
  destroy(): void {
    // Clear intervals and timers
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.promotionLockTimer) {
      clearTimeout(this.promotionLockTimer);
      this.promotionLockTimer = null;
    }
    
    // Clear promotion lock if this tab owns it
    if (this.localStorageAvailable) {
      try {
        const lockData = localStorage.getItem(this.PROMOTION_LOCK_KEY);
        if (lockData) {
          const { tabId } = JSON.parse(lockData);
          if (tabId === this.tabId) {
            localStorage.removeItem(this.PROMOTION_LOCK_KEY);
          }
        }
      } catch (error) {
        console.debug('Error cleaning up promotion lock:', error);
      }
    }
    
    // Clear cache
    this.cachedTabs = [];
    this.lastCacheUpdate = 0;
    
    // Remove from storage
    this.removeCurrentTabFromStorage();
    
    // Complete observables
    this.isDuplicateTab$.complete();
    this.primaryTabLost$.complete();
  }
}
